import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { Avatar } from '@/components/Avatar';
import { AvatarEditor } from '@/components/AvatarEditor';
import { useOutletContext } from 'react-router-dom';
import { FloorTheme } from '@/lib/themes';
import { Loader2, Camera, Shield, Mail, Key, LogOut, Eye, EyeOff, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { StartPage, normalizeStartPage } from '@/lib/startPage';

export function Settings() {
    const [user, setUser] = useState<any>(null);
    const [activeTab, setActiveTab] = useState<'profile' | 'account'>('profile');
    const [searchParams, setSearchParams] = useSearchParams();

    // Avatar State
    const [selectedImage, setSelectedImage] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [, setIsUploading] = useState(false);

    // Account State

    const [emailForm, setEmailForm] = useState({ newEmail: '', currentPassword: '' });
    const [startPage, setStartPage] = useState<StartPage>('home');
    const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
    const [accountMessage, setAccountMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
    const [accountMessageScope, setAccountMessageScope] = useState<'password' | 'email' | 'reset' | null>(null);

    // Password Reset UI State
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [resetError, setResetError] = useState<string | null>(null);

    // Context
    const context = useOutletContext<{ theme: FloorTheme }>();
    const theme = context?.theme || { colors: { primary: '#000', border: '#ddd', card: '#fff', text: '#000' } };

    useEffect(() => {
        fetchUser();
        handleVerification();
    }, []);

    const fetchUser = async () => {
        try {
            const res = await api.get('/users/me');
            const normalizedStartPage = normalizeStartPage(res.data?.start_page);
            const effectiveStartPage = normalizedStartPage === 'salon' && !res.data?.salon_enabled
                ? 'home'
                : normalizedStartPage;
            const nextUser = { ...res.data, start_page: effectiveStartPage };
            setUser(nextUser);
            setStartPage(effectiveStartPage);
            localStorage.setItem('user', JSON.stringify(nextUser));
            window.dispatchEvent(new Event('maison:user-updated'));
        } catch (err) {
            console.error(err);
        }
    };

    const handleVerification = async () => {
        const action = searchParams.get('action');
        const token = searchParams.get('token');

        if (!action || !token) return;

        try {
            if (action === 'verify_email_change') {
                await api.post('/users/me/change-email', { token });
                setMessage({ type: 'success', text: 'Email successfully changed.' });
                await fetchUser();
            } else if (action === 'reset_password') {
                // Show Password Reset Modal or Form
                // For simplicity, we can repurpose the Account tab or show a specific UI.
                // Since this page is protected (usually), but verify link might be clicked on a different device?
                // Actually `Settings` page implies logged in.
                // `reset_password` usually happens when logged out.
                // But if the user is logged in, they can also reset.
                // Let's assume for now we handle it here if logged in.
                // If not logged in, they would be redirected to login.
                // I might need a public `ResetPassword` page for better UX, but user asked for "Settings Page".
                // I will add a logic: if action=reset_password, show a specific modal to enter new password.
                setActiveTab('account');
            }
        } catch (err: any) {
            setMessage({ type: 'error', text: err.response?.data?.detail || 'Verification failed' });
        }
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            const reader = new FileReader();
            reader.onload = () => {
                setSelectedImage(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSaveAvatar = async (blob: Blob) => {
        setIsUploading(true);
        const formData = new FormData();
        formData.append('file', blob, 'avatar.jpg');
        try {
            await api.post('/users/me/avatar', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            await fetchUser();
            setMessage({ type: 'success', text: 'Avatar updated successfully.' });
        } catch (err) {
            setMessage({ type: 'error', text: 'Failed to upload avatar.' });
        } finally {
            setSelectedImage(null);
            setIsUploading(false);
        }
    };

    const handlePasswordResetRequest = async () => {
        if (!user?.email) return;
        try {
            await api.post('/auth/request-password-reset', { email: user.email });
            setAccountMessage({ type: 'success', text: 'パスワード再設定のリンクをメールで送信しました。' });
            setAccountMessageScope('password');
        } catch (err) {
            setAccountMessage({ type: 'error', text: '送信に失敗しました。時間をおいて再度お試しください。' });
            setAccountMessageScope('password');
        }
    };

    const handleEmailChangeRequest = async () => {
        try {
            await api.post('/users/me/request-email-change', {
                new_email: emailForm.newEmail,
                current_password: emailForm.currentPassword
            });
            setAccountMessage({ type: 'success', text: '新しいメールアドレス宛に確認リンクを送信しました。' });
            setAccountMessageScope('email');
            setEmailForm({ newEmail: '', currentPassword: '' });
        } catch (err: any) {
            setAccountMessage({ type: 'error', text: err.response?.data?.detail || '変更リクエストに失敗しました。' });
            setAccountMessageScope('email');
        }
    };

    const handleNewPasswordSubmit = async (newPassword: string) => {
        const token = searchParams.get('token');
        try {
            await api.post('/auth/reset-password', { token, new_password: newPassword });
            setAccountMessage({ type: 'success', text: 'パスワードを更新しました。' });
            setAccountMessageScope('reset');
            // clear params
            setSearchParams({});
        } catch (err: any) {
            setAccountMessage({ type: 'error', text: err.response?.data?.detail || 'パスワード更新に失敗しました。' });
            setAccountMessageScope('reset');
        }
    };

    const handleStartPageSave = async () => {
        try {
            await api.patch('/users/me', { start_page: startPage });
            setMessage({ type: 'success', text: '起動時の表示ページを更新しました。' });
            await fetchUser();
        } catch (err: any) {
            setMessage({ type: 'error', text: err.response?.data?.detail || '更新に失敗しました。' });
        }
    };

    if (!user) return <div className="flex h-screen items-center justify-center"><Loader2 className="animate-spin" /></div>;

    const isResettingPassword = searchParams.get('action') === 'reset_password';

    return (
        <div className="max-w-4xl mx-auto space-y-8 pb-10">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-serif font-bold mb-2">制御室</h1>
                    <p className="opacity-60 text-sm">アカウントと設定の管理</p>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 border-b" style={{ borderColor: theme.colors.border, color: user?.floor_level === 2 ? '#f8fafc' : undefined }}>
                <button
                    onClick={() => setActiveTab('profile')}
                    className={`px-6 py-3 font-medium text-sm transition-all relative ${activeTab === 'profile' ? 'opacity-100' : 'opacity-50 hover:opacity-80'
                        }`}
                >
                    プロフィール
                    {activeTab === 'profile' && (
                        <div className="absolute bottom-0 left-0 right-0 h-0.5" style={{ backgroundColor: theme.colors.primary }} />
                    )}
                </button>
                <button
                    onClick={() => setActiveTab('account')}
                    className={`px-6 py-3 font-medium text-sm transition-all relative ${activeTab === 'account' ? 'opacity-100' : 'opacity-50 hover:opacity-80'
                        }`}
                >
                    アカウント
                    {activeTab === 'account' && (
                        <div className="absolute bottom-0 left-0 right-0 h-0.5" style={{ backgroundColor: theme.colors.primary }} />
                    )}
                </button>
            </div>

            {/* Content */}
            <div className="bg-white rounded-xl border shadow-sm p-8 min-h-[400px] text-slate-900" style={{ borderColor: theme.colors.border }}>

                {/* Profile Tab */}
                {activeTab === 'profile' && !isResettingPassword && (
                    <div className="space-y-8 animate-in fade-in duration-300">
                        {message && (
                            <div className={`p-4 rounded-lg flex items-center gap-3 ${message.type === 'success' ? 'bg-green-50 text-green-900 border border-green-200' : 'bg-red-50 text-red-900 border border-red-200'
                                }`}>
                                {message.type === 'success' ? <Shield className="w-5 h-5" /> : <LogOut className="w-5 h-5" />}
                                {message.text}
                            </div>
                        )}
                        <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                            <span className="p-2 bg-gray-100 rounded-lg"><Camera className="w-5 h-5" /></span>
                            アバター設定
                        </h3>

                        <div className="flex flex-col md:flex-row items-center gap-10">
                            <div className="relative group">
                                <Avatar
                                    username={user.username}
                                    src={user.avatar_path}
                                    size="2xl"
                                    theme={theme}
                                    className="shadow-xl"
                                />
                                <button
                                    onClick={() => fileInputRef.current?.click()}
                                    className="absolute bottom-0 right-0 p-3 bg-black text-white rounded-full shadow-lg hover:scale-110 transition-transform"
                                >
                                    <Camera className="w-5 h-5" />
                                </button>
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    className="hidden"
                                    accept="image/*"
                                    onChange={handleFileSelect}
                                />
                            </div>

                            <div className="flex-1 space-y-4 text-center md:text-left">
                                <div>
                                    <h4 className="font-bold text-xl">{user.username}</h4>
                                    <p className="opacity-60">Floor {user.floor_level} Resident</p>
                                </div>
                                <p className="text-sm opacity-70 leading-relaxed max-w-sm">
                                    新しいプロフィール画像をアップロードして個性を表現しましょう。<br />
                                    保存する前に画像の切り抜きやサイズ調整が可能です。
                                </p>
                                <Button
                                    onClick={() => fileInputRef.current?.click()}
                                    variant="outline"
                                >
                                    画像を選択
                                </Button>
                            </div>
                        </div>

                        {/* Display Name Edit */}
                        <div className="pt-8 border-t" style={{ borderColor: theme.colors.border }}>
                            <h4 className="font-bold text-lg mb-4">表示名設定</h4>
                            <div className="max-w-md space-y-4">
                                <div className="space-y-2">
                                    <label className="text-sm font-medium opacity-70">表示名 (Display Name)</label>
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            value={user.display_name || ''}
                                            onChange={(e) => setUser({ ...user, display_name: e.target.value })}
                                            className="flex-1 p-2 border rounded-lg"
                                            placeholder={user.username}
                                        />
                                        <Button
                                            onClick={async () => {
                                                try {
                                                    await api.patch(`/users/me`, { display_name: user.display_name });
                                                    setMessage({ type: 'success', text: '表示名を更新しました。' });
                                                    await fetchUser();
                                                } catch (e) {
                                                    setMessage({ type: 'error', text: '更新に失敗しました。' });
                                                }
                                            }}
                                            className="whitespace-nowrap bg-black text-white"
                                        >
                                            保存
                                        </Button>
                                    </div>
                                    <p className="text-xs opacity-50">
                                        ※ この名前は「謁見の間」やタイムラインで優先的に表示されます。
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="pt-8 border-t" style={{ borderColor: theme.colors.border }}>
                            <h4 className="font-bold text-lg mb-4">起動時の最初のページ</h4>
                            <div className="max-w-md space-y-3">
                                <label className="text-sm font-medium opacity-70">最初に開くページ</label>
                                <div className="flex gap-2">
                                    <select
                                        value={startPage}
                                        onChange={(e) => setStartPage(e.target.value as StartPage)}
                                        className="flex-1 p-2 border rounded-lg bg-white"
                                    >
                                        <option value="home">マイページ（SNS投稿ページ）</option>
                                        <option value="settings">設定ページ</option>
                                        {user?.salon_enabled && (
                                            <option value="salon">サロンページ</option>
                                        )}
                                    </select>
                                    <Button
                                        onClick={handleStartPageSave}
                                        className="whitespace-nowrap bg-black text-white"
                                    >
                                        保存
                                    </Button>
                                </div>
                                <p className="text-xs opacity-50">
                                    ログイン時・リロード時・アプリ起動時に最初に開くページとして使用されます。
                                </p>
                            </div>
                        </div>
                        <div className="pt-8 border-t" style={{ borderColor: theme.colors.border }}>
                            <h4 className="font-bold text-lg mb-4">ネイティブアプリ</h4>
                            <a
                                href="/downloads/maison-portal-native.apk?v=20260517-1809"
                                download
                                className="inline-flex w-full max-w-md items-center justify-center gap-2 rounded-lg border px-4 py-3 text-sm font-semibold transition hover:bg-gray-50"
                                style={{ borderColor: theme.colors.border, color: theme.colors.text }}
                            >
                                <Download className="w-4 h-4" />
                                Android版をダウンロード
                            </a>
                        </div>
                    </div>
                )}

                {/* Account Tab */}
                {(activeTab === 'account' || isResettingPassword) && (
                    <div className="space-y-12 animate-in fade-in duration-300">
                        {isResettingPassword ? (
                            <div className="max-w-md mx-auto space-y-4">
                                <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                                    <span className="p-2 bg-yellow-100 text-yellow-800 rounded-lg"><Key className="w-5 h-5" /></span>
                                    パスワードリセット
                                </h3>
                                <form onSubmit={(e) => {
                                    e.preventDefault();
                                    const fd = new FormData(e.currentTarget);
                                    const password = fd.get('password') as string;
                                    const confirmPassword = fd.get('confirmPassword') as string;

                                    if (password !== confirmPassword) {
                                        setResetError('パスワードが一致しません。');
                                        return;
                                    }

                                    const hasUpperCase = /[A-Z]/.test(password);
                                    const hasLowerCase = /[a-z]/.test(password);
                                    const hasNumber = /[0-9]/.test(password);

                                    if (!hasUpperCase || !hasLowerCase || !hasNumber) {
                                        setResetError('パスワードは、大文字・小文字・数字をそれぞれ1文字以上含める必要があります。');
                                        return;
                                    }

                                    setResetError(null);
                                    handleNewPasswordSubmit(password);
                                }} className="space-y-4">
                                    <div className="space-y-2">
                                        <label className="text-sm font-medium">新しいパスワード</label>
                                        <div className="relative">
                                            <input
                                                name="password"
                                                type={showPassword ? "text" : "password"}
                                                required
                                                className="w-full p-2 border rounded-lg pr-10"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword(!showPassword)}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-black"
                                            >
                                                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                            </button>
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-sm font-medium">パスワード（確認）</label>
                                        <div className="relative">
                                            <input
                                                name="confirmPassword"
                                                type={showConfirmPassword ? "text" : "password"}
                                                required
                                                className="w-full p-2 border rounded-lg pr-10"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-black"
                                            >
                                                {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                            </button>
                                        </div>
                                    </div>

                                    {resetError && (
                                        <div className="text-red-500 text-sm p-2 bg-red-50 rounded">
                                            {resetError}
                                        </div>
                                    )}

                                    <Button type="submit" className="w-full bg-black text-white">パスワードを更新</Button>
                                    {accountMessage && accountMessageScope === 'reset' && (
                                        <div className={`mt-3 p-3 rounded-lg text-sm ${accountMessage.type === 'success'
                                            ? 'bg-green-50 text-green-900 border border-green-200'
                                            : 'bg-red-50 text-red-900 border border-red-200'}`}>
                                            {accountMessage.text}
                                        </div>
                                    )}
                                </form>
                            </div>
                        ) : (
                            <>
                                {/* Password Reset Request */}
                                <div className="space-y-4">
                                    <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                                        <span className="p-2 bg-gray-100 rounded-lg"><Key className="w-5 h-5" /></span>
                                        パスワード
                                    </h3>
                                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-100 flex items-center justify-between">
                                        <div className="text-sm">
                                            <p className="font-medium">パスワード変更リクエスト</p>
                                            <p className="opacity-60">登録済みのメールアドレスにリセット用リンクを送信します。</p>
                                        </div>
                                        <Button onClick={handlePasswordResetRequest} variant="outline">
                                            リンクを送信
                                        </Button>
                                    </div>
                                    {accountMessage && accountMessageScope === 'password' && (
                                        <div className={`p-3 rounded-lg text-sm ${accountMessage.type === 'success'
                                            ? 'bg-green-50 text-green-900 border border-green-200'
                                            : 'bg-red-50 text-red-900 border border-red-200'}`}>
                                            {accountMessage.text}
                                        </div>
                                    )}
                                </div>

                                {/* Email Change */}
                                <div className={`space-y-4 ${theme.font}`}>
                                    <h3 className="font-bold text-lg mb-4 flex items-center gap-2" style={{ color: theme.colors.text }}>
                                        <span
                                            className="p-2 rounded-lg"
                                            style={{ backgroundColor: `${theme.colors.primary}1a`, color: theme.colors.primary }}
                                        >
                                            <Mail className="w-5 h-5" />
                                        </span>
                                        メールアドレス
                                    </h3>
                                    <div
                                        className="p-6 border rounded-xl space-y-4"
                                        style={{ backgroundColor: theme.colors.card, borderColor: theme.colors.border, color: theme.colors.text }}
                                    >
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <label className="text-sm font-medium opacity-70" style={{ color: theme.colors.text }}>現在のEmail</label>
                                                <input
                                                    type="text"
                                                    value={user.email}
                                                    disabled
                                                    className="w-full p-2 border rounded-lg opacity-70 cursor-not-allowed"
                                                    style={{ backgroundColor: theme.colors.background, borderColor: theme.colors.border, color: theme.colors.text }}
                                                />
                                            </div>
                                        </div>

                                        <div className="pt-4 border-t" style={{ borderColor: theme.colors.border }}>
                                            <h4 className="font-medium text-sm mb-3" style={{ color: theme.colors.text }}>メールアドレス変更</h4>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                                <div className="space-y-2">
                                                    <label className="text-sm font-medium" style={{ color: theme.colors.text }}>新しいEmail</label>
                                                    <input
                                                        type="email"
                                                        value={emailForm.newEmail}
                                                        onChange={e => setEmailForm({ ...emailForm, newEmail: e.target.value })}
                                                        className="w-full p-2 border rounded-lg bg-transparent outline-none focus:ring-1"
                                                        style={{ borderColor: theme.colors.border, color: theme.colors.text }}
                                                        placeholder="new@example.com"
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <label className="text-sm font-medium" style={{ color: theme.colors.text }}>現在のパスワード</label>
                                                    <input
                                                        type="password"
                                                        value={emailForm.currentPassword}
                                                        onChange={e => setEmailForm({ ...emailForm, currentPassword: e.target.value })}
                                                        className="w-full p-2 border rounded-lg bg-transparent outline-none focus:ring-1"
                                                        style={{ borderColor: theme.colors.border, color: theme.colors.text }}
                                                        placeholder="本人確認"
                                                    />
                                                </div>
                                            </div>
                                            <div className="flex justify-end">
                                                <Button
                                                    onClick={handleEmailChangeRequest}
                                                    className={`font-semibold ${theme.font} hover:opacity-90`}
                                                    style={{ backgroundColor: theme.colors.primary, color: theme.colors.background }}
                                                >
                                                    変更リクエスト
                                                </Button>
                                            </div>
                                            {accountMessage && accountMessageScope === 'email' && (
                                                <div className={`mt-3 p-3 rounded-lg text-sm ${accountMessage.type === 'success'
                                                    ? 'bg-green-50 text-green-900 border border-green-200'
                                                    : 'bg-red-50 text-red-900 border border-red-200'}`}>
                                                    {accountMessage.text}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>

            {/* Avatar Editor Modal */}
            {selectedImage && (
                <AvatarEditor
                    imageSrc={selectedImage}
                    onCancel={() => setSelectedImage(null)}
                    onSave={handleSaveAvatar}
                />
            )}
        </div>
    );
}
